import { useAction, useAtom } from '@reatom/npm-react'
import { AnimatePresence, motion } from 'motion/react'
import { useMemo, useState } from 'react'
import styled from 'styled-components'

import { pairFocusEvent } from '@root/entities/pair'
import { refreshWalletsReatom } from '@root/entities/wallet/atoms/wallets-refresh'
import { usePullToRefresh } from '@root/shared/ui-kit/pull-to-refresh'
import { WalletOperationsDrawer } from '@root/widgets/wallet'

import { WalletsSummaryCard } from './summary/wallets-summary-card'
import { WalletsTabs } from './wallet-tabs'
import { WalletsList } from './wallets-list'
import { WalletsSwitch } from './wallets-switch'

import type { WalletAssetModel, WalletModel } from '@root/entities/wallet/atoms'

export const WalletsScreen = () => {
  const [operationsWallet, setOperationsWallet] = useState<
    WalletModel | undefined
  >(undefined)
  const refreshModel = useMemo(
    () => refreshWalletsReatom({ minWaitingTime: 1000 }),
    [],
  )
  const [walletsRefreshState] = useAtom(refreshModel.refreshStateAtom)
  const refreshWallets = useAction(refreshModel.refreshWalletsAction)

  const handlePull = () => {
    refreshWallets()
  }

  const { bindDraggable, loaderElement, yMotionValue } = usePullToRefresh({
    enabled: true,
    onPull: handlePull,
    pulling: walletsRefreshState.type === 'Loading',
  })

  const pairFocus = useAction(pairFocusEvent)

  const handleOperationsClick = (wallet: WalletModel) => {
    setOperationsWallet(wallet)
  }

  const handleAssetClick = (asset: WalletAssetModel) => {
    const { pairAddress } = asset
    if (!pairAddress) {
      return
    }
    const payload = {
      address: pairAddress,
      network: asset.currency.network,
    }
    pairFocus(payload)
  }

  return (
    <>
      {loaderElement}
      <Root style={{ y: yMotionValue }} {...bindDraggable()}>
        <Top>
          <WalletsSummaryCard />
          <Tabs>
            <WalletsTabs />
            <WalletsSwitch />
          </Tabs>
        </Top>
        <WalletsList
          onOperations={handleOperationsClick}
          onAssetClick={handleAssetClick}
        />
      </Root>
      <AnimatePresence>
        {operationsWallet ? (
          <WalletOperationsDrawer
            key={operationsWallet.id}
            wallet={operationsWallet}
            open
            onOpenChange={(open) => {
              if (!open) {
                setOperationsWallet(undefined)
              }
            }}
          />
        ) : undefined}
      </AnimatePresence>
    </>
  )
}

const Root = styled(motion.div)`
  height: 100%;
  padding-top: 14px;

  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  background: var(--background-1);
`

const Top = styled.div`
  padding-inline: 12px;
`

const Tabs = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  justify-content: space-between;
  align-items: center;
`
